/**
 * O componente Lit do `tuninho:chart` — a ponte entre o documento e o React do `recharts`.
 *
 * ## De onde vêm as linhas (T3.2)
 *
 * O card pede *"respeitando filtro e agrupamento da visão escolhida"*. Isso significa **não**
 * ler `dataSource.rows$` (o razão inteiro) e sim `view.rows$` — que já vem filtrado e
 * ordenado. Quem filtrou "Pago? = não" espera que o gráfico ao lado mostre o que está na
 * tela.
 *
 * E é `cellGetOrCreate(rowId, propertyId).value$`, não `getCell` cru: a célula de origem pode
 * ser **derivada** — outra fórmula, `title`, `created-time` — e só a `Cell` sabe disso. Ler
 * cru faria um gráfico sobre uma coluna de fórmula sair inteiro vazio, sem erro nenhum.
 *
 * ## O que acontece quando a tabela some
 *
 * `sourceBlockId` aponta para um `affine:database` do MESMO documento. Se alguém apagar a
 * tabela, o gráfico fica órfão — e **diz isso**, em vez de ficar em branco. Um bloco em
 * branco é a pior resposta possível: quem o vê não sabe se faltou configurar, se o filtro
 * zerou, ou se ele quebrou (ver `porqueNaoDaParaDesenhar`).
 */

import type { ElementOrFactory } from '@affine/component';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import { BlockComponent } from '@blocksuite/affine/std';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import { css, html, nothing, type TemplateResult } from 'lit';

import {
  analisar, avaliar,
} from '../../database-block/properties/formula/avaliador';
import { TIPO_DA_COLUNA_DE_FORMULA } from '../../database-block/properties/formula/modelo';
import type { LinhaDeDados } from './dados';
import {
  COLUNA_DE_TITULO,
  montarLinhas,
  porqueNaoDaParaDesenhar,
  TIPOS_NUMERICOS,
} from './dados';
import type { TuninhoChartBlockModel } from './model';

export const ESTILOS_DO_GRAFICO = css`
  .tuninho-chart {
    margin: 12px 0;
    padding: 12px;
    border: 1px solid var(--affine-border-color);
    border-radius: 8px;
    background: var(--affine-background-primary-color);
  }
  .tuninho-chart__titulo {
    font-size: 14px;
    font-weight: 600;
    color: var(--affine-text-primary-color);
    margin-bottom: 8px;
  }
  /* O aviso ocupa a altura do gráfico para o documento não "pular" quando ele aparece. */
  .tuninho-chart__escolha {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: center;
    padding: 8px 0 4px;
  }
  .tuninho-chart__escolha label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
  }
  .tuninho-chart__dica {
    flex-basis: 100%;
    text-align: center;
    line-height: 18px;
  }
  .tuninho-chart__dica b {
    color: var(--affine-text-primary-color);
    font-weight: 600;
  }
  .tuninho-chart__escolha select {
    font: inherit;
    font-size: 12px;
    color: var(--affine-text-primary-color);
    background: var(--affine-background-primary-color);
    border: 1px solid var(--affine-border-color);
    border-radius: 4px;
    padding: 2px 6px;
  }
  .tuninho-chart__aviso {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
    color: var(--affine-text-secondary-color);
    font-size: 13px;
    line-height: 20px;
  }
`;

/**
 * Quem sabe transformar um componente React em `TemplateResult` do Lit.
 *
 * Injetado, como o `reactToLit` do `PdfViewExtension`, e não importado: `packages/frontend/core`
 * pode montar React, mas o bloco vive numa árvore Lit, e a conversão é responsabilidade de
 * quem monta a aplicação. Importar direto amarraria o bloco a uma implementação de ponte.
 */
export type ReactParaLit = (
  elemento: ElementOrFactory,
  reciclar?: boolean
) => TemplateResult;

export class TuninhoChartBlockComponent extends BlockComponent<TuninhoChartBlockModel> {
  static override styles = ESTILOS_DO_GRAFICO;

  /** Preenchido pelo `ViewExtension` no registro. */
  reactParaLit?: ReactParaLit;

  /** Componente React do gráfico — injetado pelo mesmo caminho, pela mesma razão. */
  renderizarGrafico?: (props: {
    linhas: LinhaDeDados[];
    modelo: TuninhoChartBlockModel;
  }) => ElementOrFactory;

  /** A tabela de origem, procurada no MESMO documento. */
  private readonly _tabela$ = computed<DatabaseBlockModel | null>(() => {
    const id = this.model.props.sourceBlockId$.value;
    if (!id) return null;
    const bloco = this.store.getBlock(id);
    if (!bloco || bloco.model.flavour !== 'affine:database') return null;
    return bloco.model as DatabaseBlockModel;
  });

  /**
   * As linhas que o gráfico vê.
   *
   * ⚠️ **Um `computed` e não um `useEffect`.** A tabela muda por CRDT — outro usuário
   * lançando, ou o próprio recálculo de uma coluna de fórmula. Um efeito que "escuta e
   * atualiza" reimplementaria, pior, o que os signals já fazem: aqui, mudar uma célula
   * redesenha o gráfico sozinho, pela mesma cadeia de `cells$` que faz a fórmula recalcular.
   */
  private readonly _linhas$: ReadonlySignal<LinhaDeDados[]> = computed(() => {
    const tabela = this._tabela$.value;
    if (!tabela) return [];

    // As linhas de um `affine:database` SÃO os blocos filhos dele (`rows$ = children.map(id)`),
    // e as células vivem em `props.cells` indexadas por esse id.
    const celulas = tabela.props.cells$.value;
    const colunas = tabela.props.columns$.value;

    // A montagem é uma FUNÇÃO PURA em `dados.ts` — ver `montarLinhas` para as duas colunas
    // que não vivem em `cells` (título e fórmula) e por que cada uma custou um gráfico vazio.
    // Aqui só se traduz o modelo do BlockSuite para os tipos simples que ela recebe; é o que
    // permite testá-la nesta estação, sem navegador e sem build do Affine.
    return montarLinhas(
      colunas.map(c => ({ id: c.id, type: c.type, name: c.name, data: c.data })),
      celulas as never,
      tabela.children.map(f => ({ id: f.id, texto: f.text?.toString() ?? '' })),
      { tipoDeFormula: TIPO_DA_COLUNA_DE_FORMULA, analisar, avaliar: avaliar as never }
    );
  });

  /**
   * O seletor de eixo e de série.
   *
   * ## Por que ele existe
   *
   * Sem ele, os avisos de `porqueNaoDaParaDesenhar` — *"Escolha a coluna do eixo X"*,
   * *"Escolha ao menos uma coluna de valores"* — mandavam escolher em lugar nenhum. Um texto
   * que pede uma ação impossível é pior que um bloco em branco: ele faz quem lê procurar o
   * botão pelo resto da tarde.
   *
   * O gráfico já nasce configurado pelo item do `/` (ver `configs/slash-menu.ts`), então este
   * seletor é o caminho de TROCA, não o de conserto. Ele aparece junto com o aviso porque é
   * exatamente ali que alguém precisa dele — e some quando o gráfico desenha, para não
   * competir com o desenho.
   *
   * ⚠️ Escreve direto em `model.props`, sem `captureSync` próprio: são campos escalares
   * (Regra do `model.ts`), e o histórico do documento já agrupa a edição.
   */
  private _renderEscolha(tabela: DatabaseBlockModel) {
    const colunas = (tabela.props.columns$.value ?? []).map(c => ({
      id: c.id,
      type: c.type,
      name: c.name,
    }));
    const numericas = colunas.filter(
      c => TIPOS_NUMERICOS.includes(c.type) && c.id !== COLUNA_DE_TITULO
    );
    const serieAtual = this.model.props.series$.value?.[0]?.propertyId ?? '';
    const eixoAtual = this.model.props.eixoX$.value ?? '';

    /*
     * ⚠️ **Quando não há coluna numérica, isto aqui é uma INSTRUÇÃO, não um diagnóstico.**
     *
     * O operador mandou o print em 2026-08-28: o gráfico dizia *"Esta tabela ainda não tem
     * coluna de número ou de fórmula"* e ele respondeu *"não entendi como funciona, como
     * seleciono algo para exibir"*. A frase nomeava o que faltava e não dizia o passo — e o
     * seletor ao lado estava vazio, porque de fato não havia o que escolher.
     *
     * Dizer o QUE falta sem dizer o QUE FAZER é meia resposta, e a metade que falta é a que
     * tira a pessoa do lugar.
     */
    const semNumero = numericas.length === 0;

    return html`<div class="tuninho-chart__escolha">
      <label>
        Eixo
        <select
          @change=${(e: Event) => {
            this.model.props.eixoX = (e.target as HTMLSelectElement).value;
          }}
        >
          <option value="${COLUNA_DE_TITULO}" ?selected=${eixoAtual === COLUNA_DE_TITULO}>
            Título
          </option>
          ${colunas
            .filter(c => c.id !== COLUNA_DE_TITULO)
            .map(
              c =>
                html`<option value="${c.id}" ?selected=${eixoAtual === c.id}>
                  ${c.name || c.id}
                </option>`
            )}
        </select>
      </label>
      <label>
        Valores
        <select
          @change=${(e: Event) => {
            const id = (e.target as HTMLSelectElement).value;
            const col = numericas.find(c => c.id === id);
            this.model.props.series = col
              ? [{ propertyId: col.id, rotulo: col.name || 'Valor' }]
              : [];
          }}
        >
          <option value="" ?selected=${serieAtual === ''}>—</option>
          ${numericas.map(
            c =>
              html`<option value="${c.id}" ?selected=${serieAtual === c.id}>
                ${c.name || c.id}
              </option>`
          )}
        </select>
      </label>
      ${semNumero
        ? html`<span class="tuninho-chart__dica"
            >Para o gráfico ter o que exibir, some uma coluna de número na tabela acima: clique
            no <b>+</b> à direita dos títulos das colunas e escolha <b>Number</b>. Ela aparece
            aqui em <b>Valores</b> assim que existir.</span
          >`
        : nothing}
    </div>`;
  }

  override renderBlock() {
    const modelo = this.model;
    const titulo = modelo.props.titulo$.value;
    const tabela = this._tabela$.value;

    const cabecalho = titulo
      ? html`<div class="tuninho-chart__titulo">${titulo}</div>`
      : nothing;

    if (!tabela) {
      return html`<div class="tuninho-chart">
        ${cabecalho}
        <div class="tuninho-chart__aviso">
          ${modelo.props.sourceBlockId$.value
            ? 'A tabela que alimentava este gráfico não está mais neste documento.'
            : 'Escolha a tabela que alimenta este gráfico.'}
        </div>
      </div>`;
    }

    const linhas = this._linhas$.value;
    const motivo = porqueNaoDaParaDesenhar(linhas, {
      eixoX: modelo.props.eixoX$.value,
      series: modelo.props.series$.value,
      agregacao: modelo.props.agregacao$.value,
      tipo: modelo.props.tipo$.value,
    });

    if (motivo) {
      // O aviso diz o QUE falta; o seletor é ONDE se resolve. Separá-los foi o defeito.
      return html`<div class="tuninho-chart">
        ${cabecalho}
        <div class="tuninho-chart__aviso">${motivo}</div>
        ${this._renderEscolha(tabela)}
      </div>`;
    }

    if (!this.reactParaLit || !this.renderizarGrafico) {
      // Sem a ponte React, o bloco não desenha — e diz por quê em vez de sumir. É o caso de
      // um sabor da aplicação que não registrou a extensão (a `preview-page`, por exemplo).
      return html`<div class="tuninho-chart">
        ${cabecalho}
        <div class="tuninho-chart__aviso">
          Este gráfico não pode ser desenhado aqui — abra o documento no editor.
        </div>
      </div>`;
    }

    /*
     * ⚠️ **O seletor fica, mesmo com o gráfico desenhando.**
     *
     * Antes ele só existia no estado de aviso — ou seja, sumia exatamente quando o gráfico
     * passava a funcionar, e daí não havia como trocar o que estava sendo exibido. O operador
     * topou com isso em 2026-08-28: *"consegui adicionar mas não entendi como funciona, como
     * seleciono algo para exibir"*.
     *
     * Um controle que só aparece quando a coisa está quebrada não é controle: é mensagem de
     * erro com aparência de controle.
     */
    return html`<div class="tuninho-chart">
      ${cabecalho}
      ${this.reactParaLit(this.renderizarGrafico({ linhas, modelo }))}
      ${this._renderEscolha(tabela)}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tuninho-chart': TuninhoChartBlockComponent;
  }
}
